/**
 * Header Component
 * 
 * Project: SunMarbella Holiday Rental Website
 * Version: 1.0.0
 * Last Modified: 2025-03-09
 * 
 * Purpose:
 * Main navigation header with responsive design, language/currency selection, and mobile menu.
 * Displays compact search bar on scroll or on non-home pages. Integrates with SearchBarContext.
 * 
 * Features:
 * - Responsive navigation (desktop/mobile)
 * - Language selector (EN, ES, DE, FR)
 * - Mobile hamburger menu with overlay
 * - Compact search bar (appears on scroll or non-home pages)
 * - Logo branding with variant support
 * - WhatsApp and contact integration
 * - Scroll detection for sticky behavior
 * 
 * Dependencies:
 * - React 19.2.4 (hooks, portals)
 * - React Router 7 (navigation, location)
 * - Framer Motion 12 (animations)
 * - Font Awesome 6.5 (icons)
 * - SearchBarContext (search state management)
 * - TailwindCSS 4.1 (styling)
 * 
 * Key Props/State:
 * - isScrolled: boolean (scroll detection)
 * - showMobileMenu: boolean (mobile menu visibility)
 * - currentLang: string (selected language)
 * 
 * Routes Detected:
 * - /property/:id or /detail/:id (property pages - no compact search)
 * - / (home page - conditional compact search)
 * - Other pages (always show compact search)
 * 
 * Changelog:
 * - 1.0.0 (2025-03-09): Initial implementation with responsive design
 * - Fixed z-index issues (z-[60] for header, z-[50] for mobile overlay)
 * - Integrated SearchBarContext for state management
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { Logo } from './Logo';
import { ProIcon } from './ProIcon';
import { buildWhatsAppUrl } from '../config/company';
import { trackContactClick } from '../lib/analytics';
import { useSearchBar } from '../contexts/SearchBarContext';
import { getFavoritesCount } from '../lib/favorites';
import { SearchBarCompactSimplified } from './SearchBarCompactSimplified';
import { SimplePropertyBookingBar } from './SimplePropertyBookingBar';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [currentLang, setCurrentLang] = useState('EN');
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [showFavPulse, setShowFavPulse] = useState(false);
  const location = useLocation();
  const { isHeroSearchVisible } = useSearchBar();
  
  // Check if we're on a property detail page
  const isPropertyPage = location.pathname.startsWith('/property/') || location.pathname.startsWith('/detail/');
  
  // Check if we're on the home page
  const isHomePage = location.pathname === '/';
  
  // Check if we're on the listing page
  const isListingPage = location.pathname === '/destinations';
  
  // Show compact search:
  // - On ALL pages including property pages
  // - On home page, show when hero search is not visible (scrolled down)
  // - On all other pages, always show
  const showCompactSearch = !isHomePage || !isHeroSearchVisible;
  const showMobileOverlay = showMobileMenu;
  const languageOptions = ['EN', 'ES', 'DE', 'FR'];

  const cycleLanguage = () => {
    const currentIndex = languageOptions.indexOf(currentLang);
    const nextIndex = (currentIndex + 1) % languageOptions.length;
    setCurrentLang(languageOptions[nextIndex]);
  };

  // Update favorites count on mount and when cookie changes
  useEffect(() => {
    const updateFavCount = () => {
      const count = getFavoritesCount();
      const prevCount = favoritesCount;
      setFavoritesCount(count);
      
      // Trigger pulse animation when count increases
      if (count > prevCount) {
        setShowFavPulse(true);
        setTimeout(() => setShowFavPulse(false), 600);
      }
    };
    
    updateFavCount();
    
    // Poll for changes every 500ms (cookie changes from other components)
    const interval = setInterval(updateFavCount, 500);
    return () => clearInterval(interval);
  }, [favoritesCount]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!target.closest('.language-dropdown')) {
        setShowLangDropdown(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Determine if we should use the white variant of the logo and text
  // We use white when on the homepage AND not scrolled
  const useWhiteVariant = isHomePage && !isScrolled;
  const shouldUseSolidHeader = !isHomePage || isScrolled;

  return (
    <>
    <header 
      className={`fixed top-0 left-0 z-[60] w-full transition-all duration-300 isolate ${
        shouldUseSolidHeader 
          ? 'bg-white backdrop-blur-md border-b border-slate-100 ui-mobile-header-scrolled' 
          : 'bg-transparent ui-mobile-header-top'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            {/* Desktop: Full logo */}
            <div className="hidden md:block">
              <Logo 
                variant="full" 
                color={useWhiteVariant ? 'white' : 'colored'} 
                className="h-10 md:h-11 lg:h-12 w-auto" 
              />
            </div>
            {/* Mobile: Icon only */}
            <div className="block md:hidden">
              <Logo 
                variant="icon" 
                color={useWhiteVariant ? 'white' : 'colored'} 
                className="h-10 w-10" 
              />
            </div>
          </Link>

          {/* Mobile Compact Search - inline in header row */}
          {showCompactSearch && (
            <div className="md:hidden flex-1 min-w-0 px-2 max-w-[200px] sm:max-w-[240px]">
              <SearchBarCompactSimplified className="w-full" />
            </div>
          )}

          {/* Desktop Nav */}
          <nav className={`hidden md:flex items-center ${showCompactSearch ? 'flex-1 justify-center px-6' : 'gap-1'}`}>
            {showCompactSearch ? (
              <SearchBarCompactSimplified className="w-full max-w-xl" />
            ) : (
              <>
                <Link 
                  to="/destinations" 
                  className={`relative px-4 py-2 text-sm font-bold transition-all rounded-lg group ${
                    useWhiteVariant 
                      ? 'text-white hover:text-white' 
                      : 'text-slate-700 hover:text-brand-ocean'
                  }`}
                >
                  <span className="relative z-10">All Rentals</span>
                  <div className={`absolute inset-0 rounded-lg ${
                    useWhiteVariant 
                      ? 'bg-white/0 group-hover:bg-white/10' 
                      : 'bg-brand-ocean/0 group-hover:bg-brand-ocean/10'
                  }`} />
                </Link>
                <Link 
                  to="/collections" 
                  className={`relative px-4 py-2 text-sm font-bold transition-all rounded-lg group ${
                    useWhiteVariant 
                      ? 'text-white hover:text-white' 
                      : 'text-slate-700 hover:text-brand-ocean'
                  }`}
                >
                  <span className="relative z-10">Collections</span>
                  <div className={`absolute inset-0 rounded-lg ${
                    useWhiteVariant 
                      ? 'bg-white/0 group-hover:bg-white/10' 
                      : 'bg-brand-ocean/0 group-hover:bg-brand-ocean/10'
                  }`} />
                </Link>
                <Link 
                  to="/services" 
                  className={`relative px-4 py-2 text-sm font-bold transition-all rounded-lg group ${
                    useWhiteVariant 
                      ? 'text-white hover:text-white' 
                      : 'text-slate-700 hover:text-brand-ocean'
                  }`}
                >
                  <span className="relative z-10">Guest Services</span>
                  <div className={`absolute inset-0 rounded-lg ${
                    useWhiteVariant 
                      ? 'bg-white/0 group-hover:bg-white/10' 
                      : 'bg-brand-ocean/0 group-hover:bg-brand-ocean/10'
                  }`} />
                </Link>
                <Link 
                  to="/contact" 
                  className={`relative px-4 py-2 text-sm font-bold transition-all rounded-lg group ${
                    useWhiteVariant 
                      ? 'text-white hover:text-white' 
                      : 'text-slate-700 hover:text-brand-ocean'
                  }`}
                >
                  <span className="relative z-10">Contact</span>
                  <div className={`absolute inset-0 rounded-lg ${
                    useWhiteVariant 
                      ? 'bg-white/0 group-hover:bg-white/10' 
                      : 'bg-brand-ocean/0 group-hover:bg-brand-ocean/10'
                  }`} />
                </Link>
              </>
            )}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Language Selector Only - Currency removed */}
            <div className="hidden lg:flex items-center gap-4 relative">
              {/* Language Dropdown */}
              <div className="relative language-dropdown">
                <button 
                  onClick={() => {
                    setShowLangDropdown(!showLangDropdown);
                  }}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all text-sm font-bold ${
                    useWhiteVariant 
                      ? 'border-white/30 bg-white/10 text-white hover:bg-white/20' 
                      : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <ProIcon icon="globe" family="light" className={`w-4 h-4 ${useWhiteVariant ? 'text-white' : 'text-brand-ocean'}`} />
                  <span>{currentLang}</span>
                </button>
                
                {showLangDropdown && (
                  <div className="absolute top-full right-0 mt-2 ui-card-elevated rounded-xl py-2 min-w-[120px] z-50 shadow-lg border border-slate-100">
                    {['EN', 'ES', 'DE', 'FR'].map(lang => (
                      <button
                        key={lang}
                        onClick={() => {
                          setCurrentLang(lang);
                          setShowLangDropdown(false);
                        }}
                        className={`w-full px-4 py-2 text-left text-sm font-medium transition-colors ${
                          lang === currentLang 
                            ? 'bg-brand-sun text-white' 
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {lang === 'EN' ? 'English' : lang === 'ES' ? 'Español' : lang === 'DE' ? 'Deutsch' : 'Français'}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Favorites - Hidden on mobile, shown in menu instead */}
            <Link
              to="/destinations?favorites=1"
              className={`hidden md:flex items-center justify-center w-10 h-10 rounded-full border transition-all relative ${
                useWhiteVariant
                  ? 'border-white/30 bg-white/10 text-white hover:bg-white/20'
                  : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100'
              } ${showFavPulse ? 'animate-pulse' : ''}`}
              aria-label="View favorites"
              title="View favorites"
            >
              <ProIcon 
                icon="heart" 
                family={favoritesCount > 0 ? 'solid' : 'light'}
                className={`w-4 h-4 transition-transform ${
                  useWhiteVariant ? 'text-white' : 'text-brand-ocean'
                } ${showFavPulse ? 'scale-125' : ''}`} 
              />
              {favoritesCount > 0 && (
                <span className={`absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${
                  useWhiteVariant 
                    ? 'bg-white text-brand-ocean' 
                    : 'bg-brand-sun text-white'
                } ${showFavPulse ? 'animate-bounce' : ''}`}>
                  {favoritesCount}
                </span>
              )}
            </Link>
            
            <button 
              className={`md:hidden flex items-center justify-center w-11 h-11 rounded-xl relative z-20 transition-colors ${useWhiteVariant ? 'text-white hover:bg-white/10' : 'text-slate-600 hover:bg-slate-100'}`}
              onClick={() => {
                setShowMobileMenu(!showMobileMenu);
              }}
              aria-label="Open menu"
            >
              <ProIcon icon="bars" family="light" className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
      
    </header>

      {/* Mobile Menu Overlay - Portaled to document.body to escape header's isolate stacking context */}
      {showMobileOverlay && createPortal(
        <div className="fixed inset-0 z-[9998] md:hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              setShowMobileMenu(false);
            }}
          />

          <div className="absolute inset-0 bg-white overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <Logo 
                  variant="icon" 
                  color="colored" 
                  className="h-8 w-auto" 
                />
                <button 
                  onClick={() => setShowMobileMenu(false)}
                  className="p-2 text-slate-600 hover:text-slate-900 transition-colors"
                >
                  <ProIcon icon="xmark" family="light" className="w-6 h-6" />
                </button>
              </div>
              
              {/* Navigation Links */}
              <nav className="flex-1 overflow-y-auto px-4 py-6">
                <div className="space-y-2">
                  <Link
                    to="/destinations?favorites=1"
                    className="flex items-center gap-4 px-4 py-4 rounded-2xl text-lg font-bold text-slate-700 hover:bg-brand-sun/10 hover:text-brand-sun transition-all group relative"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <div className="relative">
                      <ProIcon icon="heart" family={favoritesCount > 0 ? 'solid' : 'light'} className="w-5 h-5 text-slate-400 group-hover:text-brand-sun transition-colors" />
                      {favoritesCount > 0 && (
                        <span className="absolute -top-2 -right-2 w-5 h-5 bg-brand-sun text-white rounded-full text-xs font-bold flex items-center justify-center">
                          {favoritesCount}
                        </span>
                      )}
                    </div>
                    <span>Favorites</span>
                    {favoritesCount > 0 && (
                      <span className="ml-auto text-sm text-slate-400">({favoritesCount})</span>
                    )}
                  </Link>

                  <Link 
                    to="/destinations" 
                    className="flex items-center gap-4 px-4 py-4 rounded-2xl text-lg font-bold text-slate-700 hover:bg-brand-sun/10 hover:text-brand-sun transition-all group"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <ProIcon icon="house-building" family="light" className="w-5 h-5 text-slate-400 group-hover:text-brand-sun transition-colors" />
                    <span>All Rentals</span>
                  </Link>
                  
                  <Link 
                    to="/collections" 
                    className="flex items-center gap-4 px-4 py-4 rounded-2xl text-lg font-bold text-slate-700 hover:bg-brand-sun/10 hover:text-brand-sun transition-all group"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <ProIcon icon="circle-location-arrow" family="light" className="w-5 h-5 text-slate-400 group-hover:text-brand-sun transition-colors" />
                    <span>Collections</span>
                  </Link>
                  
                  <Link 
                    to="/services" 
                    className="flex items-center gap-4 px-4 py-4 rounded-2xl text-lg font-bold text-slate-700 hover:bg-brand-sun/10 hover:text-brand-sun transition-all group"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <ProIcon icon="concierge-bell" family="light" className="w-5 h-5 text-slate-400 group-hover:text-brand-sun transition-colors" />
                    <span>Services</span>
                  </Link>
                  
                  <Link 
                    to="/about" 
                    className="flex items-center gap-4 px-4 py-4 rounded-2xl text-lg font-bold text-slate-700 hover:bg-brand-sun/10 hover:text-brand-sun transition-all group"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <ProIcon icon="circle-info" family="light" className="w-5 h-5 text-slate-400 group-hover:text-brand-sun transition-colors" />
                    <span>About</span>
                  </Link>
                  
                  <Link 
                    to="/contact" 
                    className="flex items-center gap-4 px-4 py-4 rounded-2xl text-lg font-bold text-slate-700 hover:bg-brand-sun/10 hover:text-brand-sun transition-all group"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <ProIcon icon="envelope-open" family="light" className="w-5 h-5 text-slate-400 group-hover:text-brand-sun transition-colors" />
                    <span>Contact</span>
                  </Link>
                </div>
              </nav>
              
              {/* Footer with Language and WhatsApp */}
              <div className="border-t border-slate-100 p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] space-y-4">
                {/* WhatsApp Chat */}
                <a
                  href={buildWhatsAppUrl('Hi SUNMARBELLA, I have a question about your rentals')}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackContactClick('whatsapp', 'mobile_menu');
                    setShowMobileMenu(false);
                  }}
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-[#25D366] hover:bg-[#128C7E] text-white font-black transition-all w-full"
                  aria-label="Chat on WhatsApp"
                >
                  <ProIcon icon="whatsapp" family="brands" className="text-xl" />
                  <span>WhatsApp Us</span>
                </a>

                <button 
                  onClick={cycleLanguage}
                  className="flex items-center justify-center gap-2 px-4 py-2 rounded-2xl border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 transition-all w-full"
                  aria-label="Switch language"
                >
                  <ProIcon icon="globe" family="light" className="w-4 h-4" />
                  <span className="font-medium">{currentLang}</span>
                </button>

              </div>
            </div>
        </div>,
        document.body
      )}
    </>
  );
};
